import React from 'react';
import axios from 'axios';
import { regionalApiUrl } from 'utils/apiUrl';
import { useAuthContext } from 'contexts/AuthContext';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { useTenantIndex } from './useTenantIndex';

interface LinkedIlluminateIdsResponse {
  illuminateTenantIds: string[];
}

interface IlluminateMappingsResponse {
  entries: { linkedTenantId: string; usedInReportingInd?: boolean | null }[];
}

export interface LinkedIlluminate {
  id: string;
  /** Missing when the tenant is not in the tenant list */
  name?: string;
  region?: string;
  /** null when the mapping could not be loaded */
  usedInReporting: boolean | null;
}

export interface UseLinkedIlluminatesResult {
  loading: boolean;
  error?: Error;
  illuminates: LinkedIlluminate[];
}

/**
 * Loads the Illuminate tenants that the given tenant shares data with,
 * together with their name, region and whether the tenant is used in their reporting.
 * The links are read from the data API in the given region.
 * Names and regions of the Illuminate tenants come from the shared tenant list.
 */
export const useLinkedIlluminates = (
  tenantId?: string,
  region?: string,
): UseLinkedIlluminatesResult => {
  const { idToken } = useAuthContext();
  const { stage } = useAppConfigContext();
  const { index, error: indexError } = useTenantIndex();
  const isIndexReady = !!index || !!indexError;
  // The result remembers which tenant and region it was loaded for,
  // so a result of the previous tenant is never shown for the current one.
  const requestKey = tenantId ? `${tenantId}|${region ?? ''}` : undefined;
  const [result, setResult] = React.useState<UseLinkedIlluminatesResult & { requestKey?: string }>({
    loading: false,
    illuminates: [],
  });

  React.useEffect(() => {
    if (!tenantId || !isIndexReady) return undefined;

    let cancelled = false;
    const headers = { Authorization: `Bearer ${idToken}` };
    const dataApiUrl = (dataRegion: string, apiPath: string) =>
      regionalApiUrl({ capabilityName: 'data', region: dataRegion, apiPath, stage });

    const loadIlluminate = async (id: string): Promise<LinkedIlluminate> => {
      const illuminate = index?.getTenant(id);

      let usedInReporting: boolean | null;
      try {
        const { data } = await axios.get<IlluminateMappingsResponse>(
          dataApiUrl(
            illuminate?.region ?? region ?? 'us-east-1',
            `tenants/illuminate/${id}/mappings`,
          ),
          { headers },
        );
        const mapping = data.entries.find((entry) => entry.linkedTenantId === tenantId);
        usedInReporting = mapping?.usedInReportingInd === true;
      } catch {
        usedInReporting = null;
      }

      return { id, name: illuminate?.name, region: illuminate?.region, usedInReporting };
    };

    const load = async () => {
      try {
        const { data } = await axios.get<LinkedIlluminateIdsResponse>(
          dataApiUrl(region ?? 'us-east-1', `tenants/${tenantId}/illuminates/`),
          { headers },
        );
        const illuminates = await Promise.all(data.illuminateTenantIds.map(loadIlluminate));
        if (!cancelled) setResult({ requestKey, loading: false, illuminates });
      } catch (error) {
        if (!cancelled) {
          setResult({ requestKey, loading: false, error: error as Error, illuminates: [] });
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [tenantId, region, requestKey, idToken, stage, index, isIndexReady]);

  if (!requestKey) return { loading: false, illuminates: [] };
  if (result.requestKey !== requestKey) return { loading: true, illuminates: [] };
  return { loading: result.loading, error: result.error, illuminates: result.illuminates };
};
